import { useState } from 'react';
import { Phone, MessageCircle, Globe, HeartHandshake, ExternalLink, LifeBuoy, X } from 'lucide-react';

/**
 * One list of support resources, shown in Settings, on the reflection journal, and at the
 * emotional-healing parts of the program. Nothing anyone writes is read or scanned — the
 * notice is simply always there, so they know where to turn without being watched.
 */
const RESOURCES = [
  {
    id: 'lifeline',
    icon: Phone,
    tint: 'purple',
    name: 'Suicide & Crisis Lifeline',
    big: '988',
    note: 'Call or text, 24/7, anywhere in the US',
    href: 'https://988lifeline.org',
    hrefLabel: '988lifeline.org',
    hrefNote: 'Chat with a trained counsellor online',
  },
  {
    id: 'crisis-text',
    icon: MessageCircle,
    tint: 'blue',
    name: 'Crisis Text Line',
    big: 'Text HOME to 741741',
    note: 'For anyone who would rather write than talk, 24/7',
    href: 'https://www.crisistextline.org',
    hrefLabel: 'crisistextline.org',
  },
  {
    id: 'samhsa',
    icon: LifeBuoy,
    tint: 'teal',
    name: 'SAMHSA National Helpline',
    big: '1-800-662-4357',
    note: 'Free, confidential, 24/7 — treatment referrals for mental health and substance use',
    href: 'https://www.samhsa.gov/find-help/national-helpline',
    hrefLabel: 'samhsa.gov',
  },
  {
    id: 'hotline',
    icon: HeartHandshake,
    tint: 'rose',
    name: 'National Domestic Violence Hotline',
    big: '1-800-799-7233',
    note: 'Or text START to 88788',
    href: 'https://www.thehotline.org/',
    hrefLabel: 'thehotline.org',
  },
  {
    id: 'international',
    icon: Globe,
    tint: 'amber',
    name: 'Outside the United States',
    big: 'findahelpline.com',
    note: 'Free, confidential helplines in your own country',
    href: 'https://findahelpline.com',
    hrefLabel: 'findahelpline.com',
  },
];

const TINTS = {
  purple: 'bg-purple-50 text-purple-700 border-purple-200',
  blue: 'bg-blue-50 text-blue-700 border-blue-200',
  teal: 'bg-teal-50 text-teal-700 border-teal-200',
  rose: 'bg-rose-50 text-rose-700 border-rose-200',
  amber: 'bg-amber-50 text-amber-700 border-amber-200',
};

export const SupportResources = () => (
  <div className="space-y-4" data-testid="support-resources">
    <p className="text-sm leading-relaxed text-gray-700">
      If this material has stirred something up, or you are having thoughts of self-harm or
      suicide, please reach for real support. Neither this platform nor your practitioner is a
      crisis service, and nothing you write here is read or monitored — so please use one of
      these, any hour of the day.
    </p>

    <div className="space-y-3">
      {RESOURCES.map(({ id, icon: Icon, tint, name, big, note, href, hrefLabel, hrefNote }) => (
        <div key={id} className={`flex items-start gap-3 rounded-lg border p-3 ${TINTS[tint]}`}
             data-testid={`support-resource-${id}`}>
          <Icon className="mt-0.5 h-5 w-5 flex-shrink-0" />
          <div className="min-w-0">
            <p className="font-semibold">{name}</p>
            <p className="text-lg font-bold">{big}</p>
            <p className="text-sm text-gray-600">{note}</p>
            {href && (
              <a href={href} target="_blank" rel="noopener noreferrer"
                 className="mt-1 inline-flex items-center gap-1 text-sm font-medium underline"
                 data-testid={`support-link-${id}`}>
                {hrefLabel} <ExternalLink className="h-3.5 w-3.5" />
              </a>
            )}
            {hrefNote && <p className="text-sm text-gray-600">{hrefNote}</p>}
          </div>
        </div>
      ))}
    </div>

    <p className="text-sm italic text-gray-600">
      You are not alone. Help is available 24/7, and your life matters.
    </p>
  </div>
);

/**
 * The quiet line that sits on the page with a button to the card. No scanning, no triggering —
 * it is simply always there.
 */
export const SupportNotice = ({ label = 'Need support?', className = '' }) => {
  const [open, setOpen] = useState(false);

  return (
    <>
      <div className={`flex flex-wrap items-center justify-between gap-3 rounded-xl border
                       border-purple-300/30 bg-purple-400/[0.07] px-4 py-3 ${className}`}
           data-testid="support-notice">
        <p className="text-sm text-purple-100/85">
          <LifeBuoy className="mr-2 inline h-4 w-4" />
          Some of this work goes deep. If you need someone to talk to, help is available 24/7.
        </p>
        <button onClick={() => setOpen(true)}
                className="rounded-full border border-purple-300/50 px-4 py-1.5 text-xs
                           text-purple-100 transition-colors hover:bg-purple-400/15"
                data-testid="support-notice-btn">
          {label}
        </button>
      </div>

      {open && (
        <div className="fixed inset-0 z-[150] flex items-center justify-center bg-black/70 p-4"
             onClick={() => setOpen(false)} data-testid="support-card">
          <div className="max-h-[85vh] w-full max-w-lg overflow-y-auto rounded-2xl bg-white p-6
                          shadow-2xl"
               onClick={(e) => e.stopPropagation()}>
            <div className="mb-4 flex items-start justify-between gap-4">
              <h3 className="text-lg font-semibold text-purple-900">Need Support?</h3>
              <button onClick={() => setOpen(false)}
                      className="text-gray-400 transition-colors hover:text-gray-700"
                      data-testid="support-card-close">
                <X className="h-5 w-5" />
              </button>
            </div>
            <SupportResources />
          </div>
        </div>
      )}
    </>
  );
};

export default SupportResources;
